import React from 'react';
import { motion } from 'framer-motion';
import { Zap, Flame } from 'lucide-react';
import { wrappedTranslations } from '../../../translations/wrapped';

interface ClutchMasterCardProps {
    closeWinPercentage: number;
    closeWins: number;
    closeMatches: number;
    bestStreak: {
        count: number;
        elo_gained: number;
    } | null;
    lang: 'en' | 'fr' | 'de' | 'it';
}

const ClutchMasterCard: React.FC<ClutchMasterCardProps> = ({
    closeWinPercentage,
    closeWins,
    closeMatches,
    bestStreak,
    lang,
}) => {
    const t = wrappedTranslations[lang];

    // Format percentage safely, handling edge cases
    const winRate = isNaN(closeWinPercentage) ? 0 : closeWinPercentage;

    return (
        <div className="h-full w-full flex flex-col items-center justify-center text-center p-6">
            <div className="max-w-4xl w-full flex flex-col items-center">
                <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring" }}
                >
                    <Zap className="w-20 h-20 md:w-32 md:h-32 mb-8 mx-auto text-[#1C7AD6]" />
                </motion.div>

                <motion.h2
                    className="text-3xl sm:text-4xl md:text-6xl font-bold mb-4 text-gray-900 tracking-tight whitespace-nowrap"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                >
                    {t.clutchMaster}
                </motion.h2>

                <motion.p
                    className="text-base md:text-xl text-gray-600 mb-12 font-light"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                >
                    {t.thrivedUnderPressure}
                </motion.p>

                <motion.div
                    className="bg-white border border-gray-200 rounded-2xl p-6 md:p-8 shadow-lg w-full max-w-3xl"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                >
                    <div className="text-center mb-10">
                        <div className="text-5xl sm:text-6xl md:text-7xl font-bold text-[#1C7AD6] mb-4 break-words">
                            {winRate.toFixed(0)}%
                        </div>
                        <div className="text-lg md:text-2xl text-gray-700 font-medium mb-6">{t.winRateInCloseMatches}</div>
                        <div className="flex justify-center gap-8 text-gray-600">
                            <div>
                                <div className="text-2xl md:text-4xl font-bold text-gray-900">{closeWins}</div>
                                <div className="text-xs md:text-sm uppercase tracking-wider">{t.wins}</div>
                            </div>
                            <div>
                                <div className="text-2xl md:text-4xl font-bold text-gray-900">{closeMatches}</div>
                                <div className="text-xs md:text-sm uppercase tracking-wider">{t.total}</div>
                            </div>
                        </div>
                    </div>

                    {bestStreak && bestStreak.count >= 3 && bestStreak.elo_gained > 0 && (
                        <motion.div
                            className="bg-gray-50 border border-gray-200 rounded-xl p-4 md:p-6 flex flex-col md:flex-row items-center justify-between gap-4"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.8 }}
                        >
                            <div className="flex items-center gap-4 md:gap-6">
                                <Flame className="w-10 h-10 md:w-12 md:h-12 text-[#1C7AD6] shrink-0" />
                                <div className="text-left">
                                    <div className="text-2xl md:text-4xl font-bold text-gray-900">{bestStreak.count}</div>
                                    <div className="text-xs md:text-sm text-gray-600 uppercase tracking-wider whitespace-nowrap">{t.matchWinStreak}</div>
                                </div>
                            </div>
                            <div className="text-center md:text-right">
                                <div className="text-2xl md:text-4xl font-bold text-emerald-600 whitespace-nowrap">+{bestStreak.elo_gained}</div>
                                <div className="text-xs md:text-sm text-gray-500">{t.eloGained}</div>
                            </div>
                        </motion.div>
                    )}
                </motion.div>
            </div>
        </div>
    );
};

export default ClutchMasterCard;
